import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Mail, ArrowLeft, BookMarked, Building2, PenLine, Send, Flag } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/StarRating";
import { FavoriteButton } from "@/components/FavoriteButton";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

function Section({ icon: Icon, title, children }) {
    return (
        <section className="py-8 border-t border-border">
            <h2 className="flex items-center gap-2 text-2xl font-serif font-medium mb-5">
                {Icon && <Icon size={22} className="text-primary" />} {title}
            </h2>
            {children}
        </section>
    );
}

function BookGrid({ books }) {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {books.map((b, i) => (
                <div key={`${b.title}-${i}`} className="group">
                    <div className="aspect-[2/3] rounded-lg overflow-hidden bg-secondary shadow-md">
                        <img src={resolveImg(b.cover_url)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                    <p className="font-serif text-lg font-medium mt-3 leading-tight">{b.title}</p>
                    {b.author && <p className="text-sm text-muted-foreground">{b.author}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{[b.publisher, b.year].filter(Boolean).join(" · ")}</p>
                </div>
            ))}
        </div>
    );
}

export default function EditorDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [editor, setEditor] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [quote, setQuote] = useState({ genre: "", word_count: "", deadline: "", message: "" });
    const [review, setReview] = useState({ rating: 5, comment: "" });
    const [report, setReport] = useState({ reason: "", details: "" });
    const [reportOpen, setReportOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        const [e, r] = await Promise.all([api.get(`/editors/${id}`), api.get(`/editors/${id}/reviews`)]);
        setEditor(e.data); setReviews(r.data);
    }, [id]);
    useEffect(() => { load(); }, [load]);

    if (!editor) return <div className="max-w-5xl mx-auto px-4 py-24 text-center text-muted-foreground">Caricamento…</div>;

    const submitQuote = async () => {
        if (!user) { toast.error("Accedi per richiedere un preventivo"); return; }
        if (!quote.message.trim()) { toast.error("Scrivi un messaggio"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/editors/${id}/quote`, quote);
            toast.success(data.email_sent ? "Richiesta inviata all'editor via email!" : "Richiesta registrata!");
            setQuoteOpen(false); setQuote({ genre: "", word_count: "", deadline: "", message: "" });
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSubmitting(false); }
    };

    const submitReview = async () => {
        if (!user) { toast.error("Accedi come scrittore per recensire"); return; }
        if (!review.comment.trim()) { toast.error("Scrivi un commento"); return; }
        setSubmitting(true);
        try {
            await api.post(`/editors/${id}/reviews`, review);
            toast.success("Recensione pubblicata!");
            setReview({ rating: 5, comment: "" });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSubmitting(false); }
    };

    const submitReport = async () => {
        if (!user) { toast.error("Accedi per segnalare"); return; }
        if (!report.reason.trim()) { toast.error("Indica un motivo"); return; }
        setSubmitting(true);
        try {
            await api.post(`/editors/${id}/report`, report);
            toast.success("Segnalazione inviata all'amministratore");
            setReportOpen(false); setReport({ reason: "", details: "" });
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSubmitting(false); }
    };

    return (
        <div>
            {/* Cover hero */}
            <div className="relative h-64 sm:h-80 overflow-hidden bg-secondary">
                {editor.cover_url && <img src={resolveImg(editor.cover_url)} alt="" className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                <Link to="/editors" className="absolute top-5 left-5 flex items-center gap-2 text-white/90 hover:text-white text-sm" data-testid="back-to-editors">
                    <ArrowLeft size={16} /> Tutti gli editor
                </Link>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row gap-6 -mt-16 relative">
                    {editor.photo_url
                        ? <img src={resolveImg(editor.photo_url)} alt={editor.name}
                            className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover border-4 border-background shadow-xl" />
                        : <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-secondary border-4 border-background shadow-xl" />}
                    <div className="sm:pt-16 flex-1">
                        <h1 className="text-4xl sm:text-5xl font-serif font-medium flex items-center gap-2 flex-wrap">{editor.name}{editor.verified && <VerifiedBadge size={24} withLabel />}</h1>
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-muted-foreground">
                            <span className="flex items-center gap-1.5"><MapPin size={16} /> {editor.location}, {editor.region}</span>
                            <span className="flex items-center gap-1.5"><StarRating rating={editor.rating} size={16} /> {editor.rating || "—"} ({editor.reviews_count} recensioni)</span>
                            <Badge variant="secondary" className="font-mono-metric">da €{editor.starting_price}/cartella</Badge>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4">
                            {editor.genres.map((g) => <span key={g} className="text-xs px-3 py-1 rounded-full bg-secondary">{g}</span>)}
                        </div>
                    </div>
                    <div className="sm:pt-16 flex items-center gap-2">
                        <FavoriteButton editorId={editor.editor_id} className="border border-border" size={20} />
                        <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
                            <DialogTrigger asChild>
                                <Button size="lg" className="bg-primary hover:bg-primary/90 gap-2 w-full sm:w-auto" data-testid="open-quote-btn">
                                    <Send size={16} /> Richiedi preventivo
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                                <DialogHeader><DialogTitle className="font-serif text-2xl">Richiedi un preventivo a {editor.name}</DialogTitle></DialogHeader>
                                {!user ? (
                                    <div className="py-6 text-center">
                                        <p className="text-muted-foreground mb-4">Accedi per inviare una richiesta di preventivo.</p>
                                        <Link to="/auth?mode=login"><Button className="bg-primary">Accedi</Button></Link>
                                    </div>
                                ) : (
                                    <div className="space-y-4 pt-2">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div><Label>Genere</Label><Input value={quote.genre} onChange={(e) => setQuote({ ...quote, genre: e.target.value })} placeholder="Es. Thriller" className="mt-1.5" data-testid="quote-genre" /></div>
                                            <div><Label>Lunghezza</Label><Input value={quote.word_count} onChange={(e) => setQuote({ ...quote, word_count: e.target.value })} placeholder="Es. 80.000 parole" className="mt-1.5" data-testid="quote-length" /></div>
                                        </div>
                                        <div><Label>Scadenza desiderata</Label><Input value={quote.deadline} onChange={(e) => setQuote({ ...quote, deadline: e.target.value })} placeholder="Es. entro marzo" className="mt-1.5" data-testid="quote-deadline" /></div>
                                        <div><Label>Messaggio</Label><Textarea value={quote.message} onChange={(e) => setQuote({ ...quote, message: e.target.value })} placeholder="Descrivi il tuo progetto…" rows={4} className="mt-1.5" data-testid="quote-message" /></div>
                                        <DialogFooter>
                                            <Button onClick={submitQuote} disabled={submitting} className="bg-primary w-full gap-2" data-testid="submit-quote-request">
                                                <Send size={16} /> {submitting ? "Invio…" : "Invia richiesta"}
                                            </Button>
                                        </DialogFooter>
                                    </div>
                                )}
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                <div className="mt-10">
                    <Section title="Bio professionale">
                        <p className="text-base leading-relaxed text-muted-foreground max-w-3xl">{editor.bio}</p>
                    </Section>

                    <Section icon={Building2} title="Case editrici con cui collabora">
                        <div className="flex flex-wrap gap-3">
                            {editor.publishers.map((p) => (
                                <span key={p} className="px-4 py-2 rounded-lg bg-card border border-border font-serif text-lg">{p}</span>
                            ))}
                        </div>
                    </Section>

                    {editor.books_edited?.length > 0 && (
                        <Section icon={BookMarked} title="Testi su cui ha lavorato">
                            <BookGrid books={editor.books_edited} />
                        </Section>
                    )}

                    {editor.own_novels?.length > 0 && (
                        <Section icon={PenLine} title="Romanzi scritti dall'editor">
                            <BookGrid books={editor.own_novels} />
                        </Section>
                    )}

                    <Section title="Tariffe e servizi">
                        <div className="grid sm:grid-cols-2 gap-4">
                            {editor.services.map((s, i) => (
                                <div key={i} className="bg-card border border-border rounded-lg p-5">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <h4 className="font-semibold">{s.name}</h4>
                                        <span className="font-mono-metric text-primary text-lg whitespace-nowrap">€{s.price} <span className="text-xs text-muted-foreground">{s.unit}</span></span>
                                    </div>
                                    {s.description && <p className="text-sm text-muted-foreground mt-2">{s.description}</p>}
                                </div>
                            ))}
                        </div>
                    </Section>

                    <Section title="Contatti">
                        <div className="flex flex-wrap items-center gap-4">
                            <a href={`mailto:${editor.contact_email}`} className="inline-flex items-center gap-2 text-primary hover:underline" data-testid="editor-email">
                                <Mail size={18} /> {editor.contact_email}
                            </a>
                            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                                <DialogTrigger asChild>
                                    <button className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-destructive transition-colors" data-testid="open-report-btn">
                                        <Flag size={15} /> Segnala questo profilo
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg">
                                    <DialogHeader><DialogTitle className="font-serif text-2xl">Segnala {editor.name}</DialogTitle></DialogHeader>
                                    {!user ? (
                                        <div className="py-6 text-center">
                                            <p className="text-muted-foreground mb-4">Accedi per segnalare un profilo all'amministrazione.</p>
                                            <Link to="/auth?mode=login"><Button className="bg-primary">Accedi</Button></Link>
                                        </div>
                                    ) : (
                                        <div className="space-y-4 pt-2">
                                            <div><Label>Motivo</Label><Input value={report.reason} onChange={(e) => setReport({ ...report, reason: e.target.value })} placeholder="Es. Profilo falso, truffa, contenuti inappropriati" className="mt-1.5" data-testid="report-reason" /></div>
                                            <div><Label>Dettagli (facoltativi)</Label><Textarea value={report.details} onChange={(e) => setReport({ ...report, details: e.target.value })} rows={3} className="mt-1.5" data-testid="report-details" /></div>
                                            <DialogFooter>
                                                <Button onClick={submitReport} disabled={submitting} className="bg-destructive hover:bg-destructive/90 w-full gap-2" data-testid="submit-report">
                                                    <Flag size={15} /> {submitting ? "Invio…" : "Invia segnalazione"}
                                                </Button>
                                            </DialogFooter>
                                        </div>
                                    )}
                                </DialogContent>
                            </Dialog>
                        </div>
                    </Section>

                    {/* Reviews */}
                    <Section title={`Recensioni dei clienti (${reviews.length})`}>
                        {user && user.role === "writer" && (
                            <div className="bg-paper border border-border rounded-lg p-5 mb-8">
                                <h4 className="font-semibold mb-3">Lascia una recensione</h4>
                                <div className="flex items-center gap-1 mb-3" data-testid="review-stars">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <button key={i} onClick={() => setReview({ ...review, rating: i })} data-testid={`review-star-${i}`}>
                                            <StarRating rating={i <= review.rating ? 5 : 0} size={24} className="!gap-0" />
                                        </button>
                                    ))}
                                    <span className="ml-2 text-sm text-muted-foreground">{review.rating}/5</span>
                                </div>
                                <Textarea value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} placeholder="Racconta la tua esperienza…" rows={3} data-testid="review-comment" />
                                <Button onClick={submitReview} disabled={submitting} className="mt-3 bg-primary gap-2" data-testid="submit-review">
                                    <PenLine size={15} /> Pubblica recensione
                                </Button>
                            </div>
                        )}
                        <div className="space-y-5">
                            {reviews.length === 0 && <p className="text-muted-foreground">Ancora nessuna recensione.</p>}
                            {reviews.map((r) => (
                                <div key={r.review_id} className="bg-card border border-border rounded-lg p-5" data-testid={`review-${r.review_id}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium">{r.author_name}</span>
                                        <StarRating rating={r.rating} size={15} />
                                    </div>
                                    <p className="text-muted-foreground mt-2 leading-relaxed">{r.comment}</p>
                                </div>
                            ))}
                        </div>
                    </Section>
                </div>
            </div>
        </div>
    );
}
